import { produce } from 'immer'
import ChangePasswordReducer, {
  Actions as ChangePasswordActions,
  ChangePasswordState,
  initialState as initialChangePasswordState,
} from 'src/app/ChangePassword/redux/changePassword.reducer'
import ManageTrustedDevicesReducer, {
  Actions as ManageTrustedDevicesActions,
  initialState as initialManageTrustedDevicesState,
  ManageTrustedDevicesState,
} from 'src/app/ManageTrustedDevices/redux/manageTrustedDevices.reducer'
import { profileSecurityRouteProps } from 'src/navigation/routeProps'
import {
  POST_RESET_PASSWORD_FAILURE,
  POST_RESET_PASSWORD_INIT,
  POST_RESET_PASSWORD_SUCCESS,
  PostResetPasswordActions,
} from 'src/redux/actions/auth.actions'
import { createBackgroundReducer } from 'src/redux/genericReducers'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface ProfileSecurityBaseState {
  emailSending: boolean
  emailSent: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
}

export const initialBaseState: ProfileSecurityBaseState = {
  emailSending: false,
  emailSent: false,
}

export type BaseActions = PostResetPasswordActions

const BaseProfileSecurityReducer = (
  state = initialState,
  action: BaseActions,
): ProfileSecurityBaseState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case POST_RESET_PASSWORD_INIT: {
        draft.emailSending = true
        draft.emailSent = false
        draft.topLevelError = undefined
        draft.errorDetailMap = undefined
        return draft
      }

      case POST_RESET_PASSWORD_SUCCESS: {
        draft.emailSending = false
        draft.emailSent = true
        return draft
      }

      case POST_RESET_PASSWORD_FAILURE: {
        draft.emailSending = false
        const errors = parseBackendErrors(action.payload)
        draft.topLevelError = errors.topLevelError
        draft.errorDetailMap = errors.errorDetailMap
        return draft
      }
    }
  })
}

export type ProfileSecurityState = ProfileSecurityBaseState & {
  changePassword: ChangePasswordState
  manageTrustedDevices: ManageTrustedDevicesState
}

export const initialState: ProfileSecurityState = {
  ...initialBaseState,
  changePassword: initialChangePasswordState,
  manageTrustedDevices: initialManageTrustedDevicesState,
}

export type Actions =
  | BaseActions
  | ChangePasswordActions
  | ManageTrustedDevicesActions

const CoreProfileSecurityReducer = (
  state = initialState,
  action: Actions,
): ProfileSecurityState => ({
  ...BaseProfileSecurityReducer(state, action as BaseActions),
  changePassword: ChangePasswordReducer(
    state.changePassword,
    action as ChangePasswordActions,
  ),
  manageTrustedDevices: ManageTrustedDevicesReducer(
    state.manageTrustedDevices,
    action as ManageTrustedDevicesActions,
  ),
})

const ProfileSecurityReducer = createBackgroundReducer(
  profileSecurityRouteProps,
  initialState,
  CoreProfileSecurityReducer,
)

export default ProfileSecurityReducer
